import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import CustomDatePicker from '../../../pages/userManagementV2/local_components/CustomDatePicker';
import { Notification } from 'services';
import { exportToExcel } from '../utils/exportToExcel';
import { fluid } from '../utils/responsive';
import useBlockScrollThrough from '../hooks/useBlockScrollThrough';

const DownloadFileModal = ({
    open,
    onClose,
    title = 'Download file',
    fields = [],
    rows = [],
    fileName = 'export',
    sheetName = 'Sheet1',
    getDate,
    // Every record, all statuses — used by "Download all …".
    allRows,
    // "Download all students" / "Download selected students".
    entityLabel = 'students',
}) => {
    // Block background scrolling when modal is open
    useBlockScrollThrough(open);

    // 'all' = every record (active + inactive); 'selected' = the list's
    // current search / filter / sort result. Both then pick fields (+ optional dates).
    const [mode, setMode] = useState(null);
    // Fields are not selected by default
    const [selectedFields, setSelectedFields] = useState(() => new Set());
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // Reset state whenever modal opens
    useEffect(() => {
        if (open) {
            setMode(null);
            setSelectedFields(new Set());
            setFromDate(null);
            setToDate(null);
        }
    }, [open]);

    const isAllSelected = useMemo(() => {
        return fields.length > 0 && selectedFields.size === fields.length;
    }, [fields, selectedFields]);

    const isIndeterminate = useMemo(() => {
        return selectedFields.size > 0 && selectedFields.size < fields.length;
    }, [fields, selectedFields]);

    const handleToggleSelectAll = useCallback(() => {
        if (isAllSelected) {
            setSelectedFields(new Set());
        } else {
            setSelectedFields(new Set(fields.map((f) => f.id)));
        }
    }, [isAllSelected, fields]);

    const handleToggleField = useCallback((fieldId) => {
        setSelectedFields((prev) => {
            const next = new Set(prev);
            if (next.has(fieldId)) {
                next.delete(fieldId);
            } else {
                next.add(fieldId);
            }
            return next;
        });
    }, []);

    const handleFromDateChange = useCallback((date) => {
        setFromDate(date);
        if (!date) {
            setToDate(null);
        } else if (toDate && moment(date).isAfter(moment(toDate), 'day')) {
            setToDate(null);
        }
    }, [toDate]);

    const isDownloadEnabled = useMemo(() => {
        if (!mode) return false;
        // Dates optional: neither, or both.
        return selectedFields.size > 0 && (!fromDate || Boolean(toDate));
    }, [mode, selectedFields, fromDate, toDate]);

    // Switching options starts fresh: fields and dates are cleared.
    const handleModeChange = useCallback((next) => {
        setMode(next);
        setSelectedFields(new Set());
        setFromDate(null);
        setToDate(null);
    }, []);

    const handleDownload = useCallback(() => {
        if (!isDownloadEnabled) {
            return;
        }

        const sourceRows = mode === 'all' ? (allRows && allRows.length ? allRows : rows) : rows;
        if (!sourceRows || sourceRows.length === 0) {
            Notification.info('No data available to download');
            return;
        }

        const toExportRow = (row, exportFields) => {
            const rowObj = {};
            exportFields.forEach((field) => {
                const val = field.getValue ? field.getValue(row) : row[field.id];
                rowObj[field.label] = (val !== undefined && val !== null && val !== '' && val !== '—') ? val : 'NA';
            });
            return rowObj;
        };

        if (fromDate && toDate && moment(fromDate).isAfter(moment(toDate), 'day')) {
            Notification.warning('From date cannot be after To date');
            return;
        }

        // Date range: "Download all" only, and only when both dates are picked.
        // "Download selected" is exactly what the list shows.
        const hasDateRange = mode === 'all' && Boolean(fromDate && toDate);
        const start = hasDateRange ? moment(fromDate).startOf('day') : null;
        const end = hasDateRange ? moment(toDate).endOf('day') : null;

        const finalRows = !hasDateRange ? sourceRows : sourceRows.filter((row) => {
            let rawDate = getDate ? getDate(row) : (row.joinedAt || row.joinedOn || row.createdAt);
            if (!rawDate) return false;
            if (typeof rawDate === 'string') {
                rawDate = rawDate.replace(/^Joined on\s*/i, '').trim();
            }
            const m = moment(rawDate);
            if (!m.isValid()) return false;
            if (m.isBefore(start) || m.isAfter(end)) return false;
            return true;
        });

        if (finalRows.length === 0) {
            Notification.info('No records match the selected date range');
            return;
        }

        // Build data containing only selected fields
        const activeFields = fields.filter((f) => selectedFields.has(f.id));
        const exportData = finalRows.map((row) => toExportRow(row, activeFields));

        exportToExcel(exportData, fileName, sheetName);
        Notification.success('File downloaded successfully');
        onClose();
    }, [isDownloadEnabled, mode, rows, allRows, fromDate, toDate, getDate, fields, selectedFields, fileName, sheetName, onClose]);

    if (!open) return null;

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: fluid(16),
                boxSizing: 'border-box',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: fluid(24),
                    width: fluid(540),
                    maxWidth: '94vw',
                    padding: `${fluid(32)} ${fluid(36)} ${fluid(28)}`,
                    boxSizing: 'border-box',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.18)',
                    position: 'relative',
                    overflow: 'visible',
                }}
            >
                {/* Title */}
                <h2
                    style={{
                        margin: 0,
                        marginBottom: fluid(22),
                        textAlign: 'center',
                        fontSize: fluid(24),
                        fontWeight: 700,
                        color: '#1E293B',
                        fontFamily: 'Roboto, sans-serif',
                    }}
                >
                    {title}
                </h2>

                {/* Download options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: fluid(14), marginBottom: mode ? fluid(22) : 0 }}>
                    {[
                        { key: 'all', label: `Download all ${entityLabel}` },
                        { key: 'selected', label: `Download selected ${entityLabel}` },
                    ].map((opt) => {
                        const isActive = mode === opt.key;
                        return (
                            <div
                                key={opt.key}
                                onClick={() => handleModeChange(opt.key)}
                                style={{ display: 'flex', alignItems: 'center', gap: fluid(10), cursor: 'pointer', userSelect: 'none' }}
                            >
                                {/* Whole-pixel sizes + absolute centring so the dot sits exactly in the middle. */}
                                <span style={{ position: 'relative', width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isActive ? '#594099' : '#AEAEAE'}`, boxSizing: 'border-box', flexShrink: 0 }}>
                                    {isActive && <span style={{ position: 'absolute', top: '50%', left: '50%', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#594099', transform: 'translate(-50%, -50%)' }} />}
                                </span>
                                <span style={{ fontSize: fluid(16), color: '#191919', fontFamily: 'Roboto, sans-serif' }}>{opt.label}</span>
                            </div>
                        );
                    })}
                </div>

                {mode && (
                <>
                {/* Select fields Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: fluid(14),
                    }}
                >
                    <span
                        style={{
                            fontSize: fluid(18),
                            fontWeight: 600,
                            color: '#334155',
                            fontFamily: 'Roboto, sans-serif',
                        }}
                    >
                        Select fields
                    </span>

                    {/* Select all toggle */}
                    <div
                        onClick={handleToggleSelectAll}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: fluid(8),
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <div
                            style={{
                                width: fluid(20),
                                height: fluid(20),
                                borderRadius: fluid(4),
                                border: isAllSelected || isIndeterminate ? '1.5px solid #594099' : '1.5px solid #AEAEAE',
                                backgroundColor: isAllSelected || isIndeterminate ? '#594099' : '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                                transition: 'all 0.15s ease',
                                flexShrink: 0,
                            }}
                        >
                            {isAllSelected && (
                                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                                    <path
                                        d="M2.5 7.5L5.5 10.5L11.5 3.5"
                                        stroke="#FFFFFF"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                            {!isAllSelected && isIndeterminate && (
                                <div
                                    style={{
                                        width: fluid(10),
                                        height: '2px',
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: '1px',
                                    }}
                                />
                            )}
                        </div>
                        <span
                            style={{
                                fontSize: fluid(16),
                                color: '#475569',
                                fontFamily: 'Roboto, sans-serif',
                            }}
                        >
                            Select all
                        </span>
                    </div>
                </div>

                {/* Fields Grid */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        columnGap: fluid(28),
                        rowGap: fluid(14),
                        marginBottom: fluid(24),
                        maxHeight: fluid(260),
                        overflowY: 'auto',
                        paddingRight: fluid(6),
                    }}
                >
                    {fields.map((field) => {
                        const isSelected = selectedFields.has(field.id);
                        return (
                            <div
                                key={field.id}
                                onClick={() => handleToggleField(field.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: fluid(10),
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    minWidth: 0,
                                }}
                            >
                                <div
                                    style={{
                                        width: fluid(20),
                                        height: fluid(20),
                                        borderRadius: fluid(4),
                                        border: isSelected ? '1.5px solid #594099' : '1.5px solid #AEAEAE',
                                        backgroundColor: isSelected ? '#594099' : '#FFFFFF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxSizing: 'border-box',
                                        transition: 'all 0.15s ease',
                                        flexShrink: 0,
                                    }}
                                >
                                    {isSelected && (
                                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                                            <path
                                                d="M2.5 7.5L5.5 10.5L11.5 3.5"
                                                stroke="#FFFFFF"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </div>
                                <span
                                    style={{
                                        fontSize: fluid(16),
                                        color: '#191919',
                                        fontFamily: 'Roboto, sans-serif',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    title={field.label}
                                >
                                    {field.label}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Date Fields (From / To) — "Download all" only */}
                {mode === 'all' && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: fluid(20),
                        position: 'relative',
                        overflow: 'visible',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: fluid(6), minWidth: 0 }}>
                        <label
                            style={{
                                fontSize: fluid(18),
                                fontWeight: 600,
                                color: '#334155',
                                fontFamily: 'Roboto, sans-serif',
                            }}
                        >
                            From <span style={{ fontSize: fluid(13), fontWeight: 400, color: '#94A3B8' }}>(optional)</span>
                        </label>
                        <CustomDatePicker
                            value={fromDate}
                            onChange={handleFromDateChange}
                            // Empty = no date filter, i.e. all data.
                            placeholder="All time"
                            variant="boxed"
                            popoverPlacement="left"
                            disableFuture={true}
                            maxDate={toDate}
                            clearable
                            onClear={() => {
                                setFromDate(null);
                                setToDate(null);
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: fluid(6), minWidth: 0 }}>
                        <label
                            style={{
                                fontSize: fluid(18),
                                fontWeight: 600,
                                color: !fromDate ? '#94A3B8' : '#334155',
                                fontFamily: 'Roboto, sans-serif',
                            }}
                        >
                            To
                        </label>
                        <CustomDatePicker
                            value={toDate}
                            onChange={setToDate}
                            placeholder="All time"
                            variant="boxed"
                            popoverPlacement="right"
                            disableFuture={true}
                            minDate={fromDate}
                            disabled={!fromDate}
                            clearable
                            onClear={() => setToDate(null)}
                        />
                    </div>
                </div>
                )}
                </>
                )}

                {/* Divider */}
                <div
                    style={{
                        height: '1px',
                        backgroundColor: '#F1F5F9',
                        margin: `${fluid(24)} 0 ${fluid(20)}`,
                    }}
                />

                {/* Buttons Footer */}
                <div style={{ display: 'flex', gap: fluid(16) }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            flex: 1,
                            height: fluid(46),
                            borderRadius: fluid(1000),
                            border: 'none',
                            backgroundColor: '#DCE4FF',
                            color: '#1E293B',
                            fontSize: fluid(16),
                            fontWeight: 600,
                            fontFamily: 'Roboto, sans-serif',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                            transition: 'opacity 0.2s',
                        }}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        disabled={!isDownloadEnabled}
                        onClick={handleDownload}
                        style={{
                            flex: 1,
                            height: fluid(46),
                            borderRadius: fluid(1000),
                            border: 'none',
                            backgroundColor: isDownloadEnabled ? '#594099' : '#E5E7EB',
                            color: isDownloadEnabled ? '#FFFFFF' : '#9CA3AF',
                            fontSize: fluid(16),
                            fontWeight: 600,
                            fontFamily: 'Roboto, sans-serif',
                            cursor: isDownloadEnabled ? 'pointer' : 'not-allowed',
                            boxSizing: 'border-box',
                            transition: 'all 0.2s',
                        }}
                    >
                        Download
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DownloadFileModal;
